import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Button,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  createTemplate,
  updateTemplate,
  fetchTemplateById,
} from '../../redux/slices/templateSlice';
import PermissionButton from '../../components/common/PermissionButton';
import { useHasPermission } from '../../hooks/usePermission';
import { getSettingValue } from '../../redux/slices/appSettingSlice';
import useAutoSaveDraft, { getAllDrafts, deleteDraft, deleteDraftsByTemplateId } from '../../hooks/useAutoSaveDraft';
import { v4 as uuidv4 } from 'uuid';
import ResumeDraftModal from '../../components/templates/ResumeDraftModal';
import { cleanupOrphanQuestions, getValidKeys, isStaticTemplate, getAllUsedKeysInTemplate } from '../../utils/templateKeyUtils';

// Tab components (will be created in next tasks)
import BasicInfoTab from '../../components/templates/BasicInfoTab';
import ContentTab from '../../components/templates/ContentTab';
import QuestionsTab from '../../components/templates/QuestionsTab';
import PreviewTab from '../../components/templates/PreviewTab';

/**
 * TemplateEditorPage - Main template editor with 4 tabs
 * 
 * Tabs:
 * 1. Basic Info - Title, description, category
 * 2. Content - CKEditor with {{key}} autocomplete
 * 3. Questions - Recursive question builder
 * 4. Preview - Live preview with test answers
 * 
 * Features:
 * - Auto-save draft (localStorage)
 * - Resume draft modal
 * - Tab disable logic
 * - Save with validation
 * - UUID generation for new questions
 */
const TemplateEditorPage = () => {
  const { templateId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { loading, currentTemplate, questionTree, error } = useSelector(
    (state) => state.templates
  );

  const { user } = useSelector((state) => state.auth);
  
  // Use useHasPermission hook for consistent permission checking (case-insensitive)
  const hasSystemAdmin = useHasPermission('systemadminpermission');
  const hasCompanyAdmin = useHasPermission('companyadminpermission');
  const hasManageCreditsPermission = useHasPermission('templates.manage_credits');

  // Helper function - Template'in createdBy'si system admin mi?
  const isTemplateCreatedBySystemAdmin = () => {
    if (!currentTemplate || !currentTemplate.createdBy) {
      return false;
    }
    
    const createdBy = currentTemplate.createdBy;
    if (typeof createdBy === 'object' && createdBy.permissions) {
      // permissions array'inde systemadminpermission var mı?
      return createdBy.permissions.some(p => {
        const code = typeof p === 'string' ? p : (p?.code || p);
        return code?.toLowerCase() === 'systemadminpermission';
      });
    }
    
    return false;
  };

  // Helper function - System admin sadece system admin'lerin şablonlarının kredi maliyetini değiştirebilir
  const canManageCreditCost = () => {
    if (!hasManageCreditsPermission || !hasSystemAdmin) {
      return false;
    }
    
    // Yeni template oluşturuluyorsa (templateId yok) → System admin kendi template'ini oluşturuyor
    if (!templateId) {
      return true;
    }
    
    // Mevcut template düzenleniyorsa → createdBy system admin mi kontrolü
    return isTemplateCreatedBySystemAdmin();
  };

  // Helper function - System admin sadece system admin'lerin şablonlarının isPublic'ini değiştirebilir
  const canManageIsPublic = () => {
    if (!hasSystemAdmin) {
      return false;
    }
    
    // Yeni template oluşturuluyorsa (templateId yok) → System admin kendi template'ini oluşturuyor
    if (!templateId) {
      return true;
    }
    
    // Mevcut template düzenleniyorsa → createdBy system admin mi kontrolü
    return isTemplateCreatedBySystemAdmin();
  };

  // Check if we're in preview mode
  const isPreviewMode = location.pathname.includes('/preview');
  
  // Helper function - template düzenlenebilir mi?
  const canEditTemplate = (template) => {
    if (!template) return false;
    if (hasSystemAdmin) return true; // System admin tümünü düzenleyebilir
    
    if (hasCompanyAdmin && user?.companyId) {
      // Company owner: kendi şirketinin şablonlarını düzenleyebilir
      const templateCompanyId = typeof template.companyId === 'object' 
        ? template.companyId?._id?.toString() 
        : template.companyId?.toString();
      return templateCompanyId === user.companyId.toString();
    }
    
    // Individual veya Company Member: sadece kendi oluşturduğu şablonları
    const templateCreatedBy = typeof template.createdBy === 'object'
      ? template.createdBy?._id?.toString()
      : template.createdBy?.toString();
    return templateCreatedBy === user?._id?.toString();
  };

  // Generate draft ID
  // For new templates: use unique UUID for each new draft (supports multiple drafts)
  // For editing: use templateId-based ID
  // Note: draftId can be updated when user resumes a draft
  const [draftId, setDraftId] = useState(() => 
    templateId ? `edit-${templateId}` : `new-${uuidv4()}`
  );

  const [templateState, setTemplateState] = useState({
    title: '',
    description: '',
    category: 'Diğer',
    contentHtml: '',
    thumbnail: '',
    isPublic: true,
    questions: [],
    creditCost: null, // Phase 7: Credit System
  });

  // In preview mode, always show preview tab (index 3)
  const [activeTab, setActiveTab] = useState(() => (isPreviewMode ? 3 : 0));

  // Orphan cleanup: Remove questions for deleted keys

  useEffect(() => {
    if (!templateState) return;
  
    // 1) Şablonda gerçekten kullanılan tüm key'leri topla
    const allValidKeys = getAllUsedKeysInTemplate(
      templateState.contentHtml,
      templateState.questions
    );
  
    // 2) Bu key listesine göre orphan soruları temizle
    const cleanedQuestions = cleanupOrphanQuestions(
      templateState.questions,
      allValidKeys
    );
  
    // 3) Sadece gerçekten soru sayısı azaldıysa state güncelle
    if (cleanedQuestions.length !== templateState.questions.length) {
      setTemplateState((prev) => ({
        ...prev,
        questions: cleanedQuestions,
      }));
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateState.contentHtml, templateState.questions]);

  
  const [saving, setSaving] = useState(false);
  const [availableDrafts, setAvailableDrafts] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [creditPriceTL, setCreditPriceTL] = useState(10); // Phase 7: Default value
  const [defaultTemplateCredits, setDefaultTemplateCredits] = useState(1); // Phase 7: Default value for template creditCost fallback

  // Auto-save draft hook
  const { clearDraft } = useAutoSaveDraft(
    templateState,
    templateState.questions,
    draftId
  );

  // Load credit system settings (Phase 7) - Always load default_template_credits for template creditCost fallback
  useEffect(() => {
    const loadCreditSettings = async () => {
      try {
        const [creditPriceResult, defaultCreditsResult] = await Promise.all([
          dispatch(getSettingValue('credit_price_tl')).unwrap().catch(() => ({ value: '10' })),
          dispatch(getSettingValue('default_template_credits')).unwrap().catch(() => ({ value: '1' }))
        ]);
        setCreditPriceTL(parseFloat(creditPriceResult?.value || '10'));
        setDefaultTemplateCredits(parseFloat(defaultCreditsResult?.value || '1'));
      } catch (error) {
        console.error('Error loading credit settings:', error);
        // Keep default values
      }
    };

    // Always load default_template_credits (needed for template creditCost fallback)
    loadCreditSettings();
  }, [dispatch]);

  // Generate temporary ID for new questions
  const generateTempId = useCallback(() => {
    return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Clean questions: Remove MongoDB fields, add _tempId, fix validation issues
  // Using a regular function (not useCallback) to avoid circular dependency in recursive calls
  const cleanQuestions = useCallback((questions) => {
    if (!questions || !Array.isArray(questions)) return [];
    
    // Helper function for recursive cleaning (defined inside to access generateTempId)
    const cleanQuestionRecursive = (questionList) => {
      if (!questionList || !Array.isArray(questionList)) return [];
      
      return questionList.map((question) => {
        // Remove MongoDB-specific fields
        const {
          _id,
          templateId,
          __v,
          createdAt,
          updatedAt,
          children,
          ...cleanQuestion
        } = question;
        
        // Generate new _tempId if not exists
        if (!cleanQuestion._tempId) {
          cleanQuestion._tempId = generateTempId();
        }
        
        // Fix validation: If repeatable is false, remove listType completely
        if (!cleanQuestion.repeatable && cleanQuestion.listType) {
          delete cleanQuestion.listType;
        }
        
        // Clean nested questions in answers
        if (cleanQuestion.answers && Array.isArray(cleanQuestion.answers)) {
          cleanQuestion.answers = cleanQuestion.answers.map((answer) => {
            if (answer.content?.nestedQuestions && Array.isArray(answer.content.nestedQuestions)) {
              // Recursively clean nested questions
              answer.content.nestedQuestions = cleanQuestionRecursive(answer.content.nestedQuestions);
            }
            return answer;
          });
        }
        
        return cleanQuestion;
      });
    };
    
    return cleanQuestionRecursive(questions);
  }, [generateTempId]);

  // Handle draft resume (from selected draft)
  const handleResumeDraft = (selectedDraft) => {
    if (selectedDraft) {
      // Remove the resumed draft from localStorage (it's now loaded into templateState)
      // This prevents the modal from reopening
      if (selectedDraft.id) {
        deleteDraft(selectedDraft.id);
      }
      
      // Update draftId to a new UUID (so it saves as a new draft, not overwriting the old one)
      // This ensures the resumed draft becomes a new draft with new UUID
      const newDraftId = `new-${uuidv4()}`;
      setDraftId(newDraftId);
      
      // Clean questions from draft (remove MongoDB fields, add _tempId, fix validation)
      const cleanedQuestions = cleanQuestions(selectedDraft.questions || []);
      
      setTemplateState({
        title: selectedDraft.template?.title || '',
        description: selectedDraft.template?.description || '',
        category: selectedDraft.template?.category || 'Diğer',
        contentHtml: selectedDraft.template?.contentHtml || '',
        thumbnail: selectedDraft.template?.thumbnail || '',
        isPublic: false, // Draft'tan template oluştururken isPublic her zaman false
        questions: cleanedQuestions,
        creditCost: selectedDraft.template?.creditCost !== undefined ? selectedDraft.template.creditCost : null // Phase 7
      });
      
      // Update available drafts list (remove the resumed one)
      const updatedDrafts = availableDrafts.filter(d => d.id !== selectedDraft.id);
      setAvailableDrafts(updatedDrafts);
      
      setSnackbar({
        open: true,
        message: 'Taslak geri yüklendi',
        severity: 'success',
      });
    }
  };

  // Handle draft discard (delete specific draft)
  const handleDiscardDraft = (draftIdToDelete) => {
    if (draftIdToDelete) {
      deleteDraft(draftIdToDelete);
      // Update available drafts list
      const updatedDrafts = availableDrafts.filter(d => d.id !== draftIdToDelete);
      setAvailableDrafts(updatedDrafts);
      
      // If no drafts left, modal will close automatically (drafts.length === 0)
      
      setSnackbar({
        open: true,
        message: 'Taslak silindi',
        severity: 'info',
      });
    } else {
      // Delete all drafts
      availableDrafts.forEach(draft => {
        deleteDraft(draft.id);
      });
      setAvailableDrafts([]);
      setSnackbar({
        open: true,
        message: 'Tüm taslaklar silindi',
        severity: 'info',
      });
    }
  };

  // Handle draft cancel (user closes modal but keeps drafts)
  const handleCancelDraft = () => {
    // Just close modal, keep drafts in localStorage
    // User can resume them later when they come back
    setAvailableDrafts([]);
  };

  // In preview mode, always show preview tab
  useEffect(() => {
    if (isPreviewMode) {
      setActiveTab(3);
    }
  }, [isPreviewMode]);

  // Load existing template or draft
  // This effect runs on every mount (when user navigates to this page)
  // IMPORTANT: We check draft on every mount, not just when dependencies change
  useEffect(() => {
    if (templateId) {
      // Load existing template
      dispatch(fetchTemplateById(templateId));
      // Clear any draft for this template (we're loading from server)
      clearDraft();
      // Reset available drafts
      setAvailableDrafts([]);
    } else if (!isPreviewMode) {
      // Check for all drafts (new template) - ALWAYS check when component mounts
      // This ensures modal shows even if user closed it before and came back
      const drafts = getAllDrafts();
      
      // Filter drafts that have content
      const draftsWithContent = drafts.filter(draft => {
        const hasContent = 
          (draft.template?.title && draft.template.title.trim() !== '') ||
          (draft.template?.contentHtml && draft.template.contentHtml.trim() !== '') ||
          (draft.questions && Array.isArray(draft.questions) && draft.questions.length > 0);
        return hasContent;
      });
      
      // Debug: log draft check
      console.log('Draft check on mount:', { draftId, totalDrafts: drafts.length, draftsWithContent: draftsWithContent.length, drafts: draftsWithContent });
      
      if (draftsWithContent.length > 0) {
        // Show modal with all available drafts
        setAvailableDrafts(draftsWithContent);
        // Don't set draftToResume here - let user select from modal
      } else {
        // No drafts with content
        setAvailableDrafts([]);
      }
    }
    // Note: We intentionally don't include clearDraft in dependencies
    // because we want this to run on every mount, not just when clearDraft changes
    // We also include location.key to ensure this runs when user navigates back to this page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, draftId, dispatch, location.key]);

  // Update state when template is loaded
  useEffect(() => {
    if (templateId && currentTemplate && questionTree) {
      // Only load template data if we're editing (templateId exists)
      setTemplateState({
        title: currentTemplate.title || '',
        description: currentTemplate.description || '',
        category: currentTemplate.category || 'Diğer',
        contentHtml: currentTemplate.contentHtml || '',
        thumbnail: currentTemplate.thumbnail || '',
        isPublic: currentTemplate.isPublic !== undefined ? currentTemplate.isPublic : true,
        questions: flattenQuestionTree(questionTree),
        creditCost: currentTemplate.creditCost !== undefined ? currentTemplate.creditCost : null, // Phase 7
      });
    } else if (!templateId) {
      // If no templateId (new template), reset state to empty
      setTemplateState({
        title: '',
        description: '',
        category: 'Diğer',
        contentHtml: '',
        thumbnail: '',
        isPublic: true,
        questions: [],
      });
      // Reset draft ID for new template
      setDraftId(`new-${uuidv4()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, currentTemplate, questionTree]);

  // Flatten question tree to array (for editing)
  // CRITICAL: Preserve nested questions in answers[].content.nestedQuestions
  const flattenQuestionTree = useCallback((tree) => {
    if (!tree || !Array.isArray(tree)) return [];
    
    const flattened = [];
    const questionMap = new Map(); // Map to store questions by their unique key
    
    // First pass: Collect all questions (including nested) and add to flattened array
    const collectQuestions = (nodes, parentId = null) => {
      nodes.forEach((node) => {
        const questionId = node._id ? (typeof node._id === 'string' ? node._id : node._id.toString()) : node._tempId;
        const _tempId = questionId || generateTempId();
        const uniqueKey = `${_tempId}_${parentId || 'root'}`;
        
        // Skip if already collected
        if (questionMap.has(uniqueKey)) {
          return;
        }
        
        const question = {
          ...node,
          _tempId: _tempId,
          parentId: parentId,
        };
        
        questionMap.set(uniqueKey, question);
        flattened.push(question);
        
        // Recursively collect nested questions from answers
        if (node.answers && Array.isArray(node.answers)) {
          node.answers.forEach((answer) => {
            if (answer.content?.nestedQuestions && Array.isArray(answer.content.nestedQuestions)) {
              collectQuestions(answer.content.nestedQuestions, _tempId);
            }
          });
        }
      });
    };
    
    collectQuestions(tree);
    
    // Second pass: Update questions with preserved answers and nestedQuestions references
    // CRITICAL: Use the original tree structure, not questionMap
    const updateQuestionFromTree = (node, question) => {
      if (node.answers && Array.isArray(node.answers)) {
        question.answers = node.answers.map((answer) => {
          const answerCopy = { ...answer };
          
          // Preserve nestedQuestions from answer.content.nestedQuestions
          if (answer.content?.nestedQuestions && Array.isArray(answer.content.nestedQuestions)) {
            // Map nested questions to their flattened versions
            const nestedQuestions = answer.content.nestedQuestions.map((nestedQ) => {
              const nestedId = nestedQ._id ? (typeof nestedQ._id === 'string' ? nestedQ._id : nestedQ._id.toString()) : nestedQ._tempId;
              const nestedTempId = nestedId || generateTempId();
              
              // Find the nested question in flattened array
              // Try multiple matching strategies
              const nestedQuestion = flattened.find(q => {
                // Match by _tempId and parentId
                if (q._tempId === nestedTempId && q.parentId === question._tempId) {
                  return true;
                }
                // Match by key and parentId (for cases where _id might be different)
                if (q.key === nestedQ.key && q.parentId === question._tempId) {
                  return true;
                }
                // Match by _id and parentId (if _id exists)
                if (nestedQ._id && q._id && q._id.toString() === nestedQ._id.toString() && q.parentId === question._tempId) {
                  return true;
                }
                return false;
              });
              
              if (nestedQuestion) {
                return {
                  ...nestedQuestion,
                  parentAnswerValue: answer.value,
                  _isNested: true,
                };
              }
              
              // If not found, return the nested question with proper metadata
              return {
                ...nestedQ,
                _tempId: nestedTempId,
                parentId: question._tempId,
                parentAnswerValue: answer.value,
                _isNested: true,
              };
            });
            
            answerCopy.content = {
              ...answer.content,
              nestedQuestions: nestedQuestions,
            };
          }
          
          return answerCopy;
        });
      }
    };
    
    // Traverse tree and update questions
    const traverseAndUpdate = (nodes) => {
      nodes.forEach((node) => {
        const questionId = node._id ? (typeof node._id === 'string' ? node._id : node._id.toString()) : node._tempId;
        const _tempId = questionId || generateTempId();
        
        // Find question in flattened array
        const question = flattened.find(q => 
          q._tempId === _tempId || 
          (q._id && node._id && q._id.toString() === node._id.toString()) ||
          (q.key === node.key && !q.parentId && !node.parentId)
        );
        
        if (question) {
          updateQuestionFromTree(node, question);
          
          // Recursively process nested questions
          if (node.answers && Array.isArray(node.answers)) {
            node.answers.forEach((answer) => {
              if (answer.content?.nestedQuestions && Array.isArray(answer.content.nestedQuestions)) {
                traverseAndUpdate(answer.content.nestedQuestions);
              }
            });
          }
        }
      });
    };
    
    traverseAndUpdate(tree);
    
    return flattened;
  }, [generateTempId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Validation
      if (!templateState.title || templateState.title.trim() === '') {
        setSnackbar({
          open: true,
          message: 'Şablon başlığı gerekli',
          severity: 'error',
        });
        setSaving(false);
        return;
      }

      // Check if template is static (no keys)
      const isStatic = isStaticTemplate(templateState.contentHtml);
      
      // If not static, all valid keys must have questions defined
      if (!isStatic) {
        const validKeys = getValidKeys(templateState.contentHtml);
        const definedKeys = templateState.questions.map(q => q.key).filter(Boolean);
        const missingKeys = validKeys.filter(key => !definedKeys.includes(key));
        
        if (missingKeys.length > 0) {
          setSnackbar({
            open: true,
            message: `Tüm key'ler için soru tanımlanmalı. Eksik key'ler: ${missingKeys.join(', ')}`,
            severity: 'error',
          });
          setSaving(false);
          return;
        }
      }

      // Clean questions before saving (remove MongoDB fields, fix validation issues)
      const cleanedQuestions = cleanQuestions(templateState.questions);
      
      // Ensure all questions have _tempId
      const questionsWithTempIds = cleanedQuestions.map((q) => ({
        ...q,
        _tempId: q._tempId || q._id || generateTempId(),
      }));

      // Prepare template data
      const templateData = {
        title: templateState.title.trim(),
        description: templateState.description || '',
        category: templateState.category,
        contentHtml: templateState.contentHtml || '',
        thumbnail: templateState.thumbnail || '',
        isPublic: templateState.isPublic,
        // Phase 7: Credit System - Null veya boş gelirse default_template_credits kullanılır, o da yoksa 1
        creditCost: templateState.creditCost !== null && templateState.creditCost !== undefined 
          ? templateState.creditCost 
          : defaultTemplateCredits,
      };

      // Save
      let result;
      if (templateId) {
        result = await dispatch(
          updateTemplate({
            id: templateId,
            template: templateData,
            questions: questionsWithTempIds,
          })
        ).unwrap();
      } else {
        result = await dispatch(
          createTemplate({
            template: templateData,
            questions: questionsWithTempIds,
          })
        ).unwrap();
      }

      // Clear current draft
      clearDraft();
      
      // Delete all drafts for this template ID (if template was saved/updated)
      if (result && result.template && result.template._id) {
        const savedTemplateId = result.template._id;
        deleteDraftsByTemplateId(savedTemplateId);
      } else if (templateId) {
        // If updating existing template, delete drafts by current templateId
        deleteDraftsByTemplateId(templateId);
      }
      
      setSnackbar({
        open: true,
        message: 'Şablon başarıyla kaydedildi',
        severity: 'success',
      });

      // Navigate to list after short delay
      setTimeout(() => {
        history.push('/templates');
      }, 1000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Şablon kaydedilemedi',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    history.push('/templates');
  };

  const handleEdit = () => {
    if (templateId) {
      history.push(`/templates/${templateId}/edit`);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Tab disable logic
  const isContentTabDisabled = !templateState.title || templateState.title.trim() === '';
  const isQuestionsTabDisabled = !templateState.title || templateState.title.trim() === '';
  
  // Preview is disabled if not static and not all keys have questions defined
  const isStatic = isStaticTemplate(templateState.contentHtml);
  const validKeys = getValidKeys(templateState.contentHtml);
  const definedKeys = templateState.questions.map(q => q.key).filter(Boolean);
  const missingKeys = validKeys.filter(key => !definedKeys.includes(key));
  const isPreviewTabDisabled = !isStatic && missingKeys.length > 0;

  return (
    <>
      {/* Resume Draft Modal - Hide in preview mode */}
      {!isPreviewMode && (
        <ResumeDraftModal
          drafts={availableDrafts}
          onResume={handleResumeDraft}
          onDiscard={handleDiscardDraft}
          onCancel={handleCancelDraft}
        />
      )}

      <Container maxWidth="xl">
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Geri Dön
      </Button>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {isPreviewMode ? 'Şablon Önizleme' : (templateId ? 'Şablon Düzenle' : 'Yeni Şablon')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isPreviewMode 
              ? 'Şablonun nasıl göründüğünü önizleyin' 
              : (templateId ? 'Şablon bilgilerini düzenleyin' : 'Yeni bir şablon oluşturun')}
          </Typography>
        </Box>
        {isPreviewMode ? (
          canEditTemplate(currentTemplate) && (
            <PermissionButton
              permission="templates.edit"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              disabled={!templateId}
            >
              Düzenle
            </PermissionButton>
          )
        ) : (
          <PermissionButton
            permission={templateId ? 'templates.edit' : 'templates.create'}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </PermissionButton>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs - Hide in preview mode */}
      {!isPreviewMode && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="template editor tabs"
          >
            <Tab label="Temel Bilgiler" />
            <Tab label="İçerik" disabled={isContentTabDisabled} />
            <Tab label="Sorular" disabled={isQuestionsTabDisabled} />
            <Tab label="Önizleme" disabled={isPreviewTabDisabled} />
          </Tabs>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tab Panels */}
      {!loading && (
        <>
          {isPreviewMode ? (
            // Preview mode: Only show Preview tab
            <Box sx={{ height: 'calc(100vh - 300px)', minHeight: 0 }}>
              <PreviewTab
                content={templateState.contentHtml}
                questions={templateState.questions}
                questionTree={null}
              />
            </Box>
          ) : (
            // Edit mode: Show all tabs
            <>
              {activeTab === 0 && (
                <BasicInfoTab
                  template={templateState}
                  onChange={(field, value) =>
                    setTemplateState((prev) => ({ ...prev, [field]: value }))
                  }
                  showIsPublic={canManageIsPublic()}
                  showCreditCost={canManageCreditCost()}
                  creditPriceTL={creditPriceTL}
                  defaultTemplateCredits={defaultTemplateCredits}
                />
              )}

              {activeTab === 1 && (
                <ContentTab
                  content={templateState.contentHtml}
                  questions={templateState.questions}
                  onChange={(html) =>
                    setTemplateState((prev) => ({ ...prev, contentHtml: html }))
                  }
                />
              )}

              {activeTab === 2 && (
                <QuestionsTab
                  content={templateState.contentHtml}
                  questions={templateState.questions}
                  onChange={(questions) =>
                    setTemplateState((prev) => ({ ...prev, questions }))
                  }
                />
              )}

              {activeTab === 3 && (
                <Box sx={{ height: 'calc(100vh - 300px)', minHeight: 0 }}>
                  <PreviewTab
                    content={templateState.contentHtml}
                    questions={templateState.questions}
                    questionTree={null}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default TemplateEditorPage;

